import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileUp, Loader2, MonitorDown, PencilLine } from "lucide-react";
import { errMsg } from "@/lib/api";
import useAdminApi from "@/hooks/useAdminApi";
import { POLICIES, policyToText } from "@/pages/policies";

const isData = (v) => typeof v === "string" && v.startsWith("data:image/");

const saveJson = (obj, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(obj)], { type: "application/json" }));
  a.download = name;
  a.click();
};

const countNew = (d) =>
  [d.settings.qr_image, ...d.properties.flatMap((p) => p.images || []), ...d.rooms.map((r) => r.image)].filter(isData).length;

export default function OfflineTab() {
  const call = useAdminApi();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const editor = useRef({ win: null, data: null, ready: false });

  const getData = async () => {
    const d = await call("get", "/admin/export");
    Object.entries(POLICIES).forEach(([k, p]) => {
      if (!d.settings[`policy_${k}`]) d.settings[`policy_${k}`] = policyToText(p);
    });
    return d;
  };

  const exportData = async () => {
    try {
      saveJson(await getData(), `ananta-website-data-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const editLive = async () => {
    const win = window.open("/ananta-offline-admin.html", "_blank");
    editor.current = { win, data: null, ready: false };
    if (!win) {
      toast.error("Your browser blocked the editor window. Allow pop-ups for this site and try again.");
      return;
    }
    try {
      const data = await getData();
      editor.current = { ...editor.current, win, data };
      if (editor.current.ready) win.postMessage({ type: "ananta-data", data }, window.location.origin);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pick = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    f.text().then((t) => {
      try {
        const d = JSON.parse(t);
        if (d.format !== "ananta-offline-v1") throw new Error();
        setFile({ name: f.name, data: d, photos: countNew(d) });
      } catch {
        toast.error("This is not an Ananta website data file");
      }
    });
    e.target.value = "";
  };

  const publish = async (src) => {
    const d = structuredClone(src);
    const up = async (v) => {
      if (!isData(v)) return v;
      setBusy(`Uploading photos…`);
      return (await call("post", "/admin/media", { data_url: v })).url;
    };
    try {
      d.settings.qr_image = await up(d.settings.qr_image);
      d.settings.testimonials = (d.settings.testimonials || []).filter((t) => t.name?.trim() && t.text?.trim().length >= 3);
      for (const p of d.properties) p.images = await Promise.all((p.images || []).map(up));
      for (const r of d.rooms) r.image = await up(r.image);
      setBusy("Publishing…");
      const res = await call("post", "/admin/import", { format: d.format, settings: d.settings, properties: d.properties.filter((p) => !p.coming_soon), rooms: d.rooms });
      toast.success(`Website updated: ${res.properties_updated} hotels, ${res.rooms_updated} rooms`);
      setFile(null);
      return `Published! Your website is updated (${res.properties_updated} hotels, ${res.rooms_updated} rooms).`;
    } catch (e) {
      toast.error(errMsg(e, "Import failed. Nothing was published."));
      return errMsg(e, "Publishing failed. Nothing was changed on the website.");
    } finally {
      setBusy("");
    }
  };

  const publishRef = useRef(publish);
  publishRef.current = publish;
  useEffect(() => {
    const onMsg = async (e) => {
      const { win, data } = editor.current;
      if (!win || e.source !== win || e.origin !== window.location.origin || !e.data) return;
      if (e.data.type === "ananta-ready") {
        editor.current.ready = true;
        if (data) win.postMessage({ type: "ananta-data", data }, window.location.origin);
      }
      if (e.data.type === "ananta-publish" && e.data.data?.format === "ananta-offline-v1") {
        const msg = await publishRef.current(e.data.data);
        win.postMessage({ type: "ananta-published", msg }, window.location.origin);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  return (
    <div className="space-y-5" data-testid="admin-offline-tab">
    <div className="bg-[#1E3B2E] text-[#FDFBF7] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 className="text-2xl sm:text-3xl">Edit your website</h3>
        <p className="text-sm opacity-80 mt-2 max-w-xl">Opens the editor with your current website loaded. Change photos, prices and text, then click "Save & publish" there and your live website updates right away. Password: your offline admin password.</p>
      </div>
      <button onClick={editLive} className="btn-primary !bg-[#C28B5E] shrink-0" data-testid="admin-edit-website-btn"><PencilLine size={15} />Edit website</button>
    </div>
    <p className="eyebrow text-[#6b6b6b]">Or work fully offline with files</p>
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="bg-white border border-[#E2DCD0] p-6">
        <p className="eyebrow text-[#C28B5E]">Step 1</p>
        <h3 className="text-2xl text-[#1E3B2E] mt-1">Get the offline tool</h3>
        <p className="text-sm text-[#6b6b6b] mt-2">Download the offline admin and your current website data. Open the tool on your computer, with no internet needed.</p>
        <a href="/ananta-offline-admin.html" download="ananta-offline-admin.html" className="btn-primary mt-5 w-full" data-testid="admin-download-offline-tool"><MonitorDown size={15} />Offline admin tool</a>
        <button onClick={exportData} className="btn-ghost mt-3 w-full text-[#1E3B2E]" data-testid="admin-export-data-btn"><Download size={15} />Website data file</button>
      </div>
      <div className="bg-white border border-[#E2DCD0] p-6">
        <p className="eyebrow text-[#C28B5E]">Step 2</p>
        <h3 className="text-2xl text-[#1E3B2E] mt-1">Edit offline</h3>
        <p className="text-sm text-[#6b6b6b] mt-2">In the tool, open the data file and change photos, rooms, prices, contact numbers, payment QR, check-in info and website text. Changes are saved only on your computer. When done, click "Download updated file".</p>
      </div>
      <div className="bg-white border border-[#E2DCD0] p-6">
        <p className="eyebrow text-[#C28B5E]">Step 3</p>
        <h3 className="text-2xl text-[#1E3B2E] mt-1">Import to publish</h3>
        <p className="text-sm text-[#6b6b6b] mt-2">The website only changes when you import the updated file here.</p>
        <label className="btn-ghost mt-5 w-full text-[#1E3B2E] cursor-pointer" data-testid="admin-import-file-label"><FileUp size={15} />Choose updated file<input type="file" accept=".json,application/json" className="hidden" onChange={pick} data-testid="admin-import-file-input" /></label>
        {file && (
          <div className="mt-4 text-sm border-t border-[#E2DCD0] pt-4" data-testid="admin-import-summary">
            <p className="break-all"><strong>{file.name}</strong></p>
            <p className="text-[#6b6b6b]">{file.data.properties.length} hotels · {file.data.rooms.length} rooms · {file.photos} new photo(s)</p>
            <button onClick={() => publish(file.data)} disabled={!!busy} className="btn-primary mt-4 w-full" data-testid="admin-import-publish-btn">{busy ? <><Loader2 size={15} className="animate-spin" />{busy}</> : "Publish to website"}</button>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
